import { useId, useState, type FormEvent } from 'react';
import { HttpError } from '../../api.js';
import { LEER, feldText, istNurLesen, patchAus, wertLabel, wertText, zuFormular, type FeldDef } from './crm-felder.js';

/** Lesedarstellung: alle Felder einer Definition, leere Werte sichtbar als „—“ */
export function Werte({ defs, werte, spalten = 2 }: { defs: readonly FeldDef[]; werte: Record<string, unknown>; spalten?: 1 | 2 }) {
  return (
    <dl className={`crm-werte crm-werte--${spalten}`}>
      {defs.map((d) => (
        <div key={d.key} className={`crm-wert${d.art === 'ro-tech' ? ' crm-wert--tech' : ''}`}>
          <dt>
            {d.label}
            {d.herkunft && <span className="crm-herkunft"> · {d.herkunft}</span>}
          </dt>
          <dd className={d.art === 'int' || d.art === 'dezimal' || d.art === 'zeit' || d.art === 'ro-zeit' || d.art === 'datum' ? 'pb-num' : undefined}>
            {d.art === 'textarea' && werte[d.key] ? (
              <span className="crm-mehrzeilig">{String(werte[d.key])}</span>
            ) : d.art === 'select' && typeof werte[d.key] === 'string' ? (
              <span title={String(werte[d.key])}>{d.optionLabels?.[werte[d.key] as string] ?? wertLabel(d.key, werte[d.key])}</span>
            ) : (
              feldText(d.key, werte[d.key], d.art)
            )}
          </dd>
        </div>
      ))}
    </dl>
  );
}

/**
 * Generisches Formular je Maske: ein Eingabefeld je Felddefinition (Selects für CHECK-Werte, Checkbox für bool,
 * datetime-local für Zeitpunkte). Speichern liefert nur geänderte Felder (PATCH) bzw. alle gesetzten (POST).
 */
export function Formular({
  defs,
  original,
  fest = [],
  neu = false,
  titel,
  onSpeichern,
  onAbbrechen,
}: {
  defs: readonly FeldDef[];
  original: Record<string, unknown>;
  fest?: readonly string[];
  neu?: boolean;
  titel: string;
  onSpeichern(patch: Record<string, unknown>, kommentar: string): Promise<void>;
  onAbbrechen(): void;
}) {
  const [werte, setWerte] = useState<Record<string, string>>(() => Object.fromEntries(defs.map((d) => [d.key, zuFormular(d, original[d.key])])));
  const [kommentar, setKommentar] = useState('');
  const [busy, setBusy] = useState(false);
  const [fehler, setFehler] = useState<{ text: string; feld: string | null } | null>(null);
  const basisId = useId();
  const kommentarId = useId();

  const schreibbar = defs.filter((d) => !istNurLesen(d) && !(neu ? false : fest.includes(d.key)));
  // Vergleich auf Formularebene: nur tatsächlich geänderte Felder, unberührte Zeitpunkte bleiben samt Sekunden erhalten
  const patch = patchAus(schreibbar, original, werte, []);
  const anzahl = Object.keys(patch).length;

  async function senden(e: FormEvent) {
    e.preventDefault();
    setFehler(null);
    setBusy(true);
    try {
      await onSpeichern(patch, kommentar.trim());
    } catch (err) {
      const code = err instanceof HttpError ? err.code : null;
      const feld = code?.startsWith('crm.eingabe.') ? code.slice('crm.eingabe.'.length) : null;
      setFehler({ text: err instanceof HttpError ? (err.text ?? `Speichern fehlgeschlagen (${err.status}).`) : 'Speichern fehlgeschlagen. Bitte versuchen Sie es erneut.', feld });
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="crm-form" onSubmit={(e) => void senden(e)} noValidate aria-busy={busy} aria-label={titel}>
      <div className="crm-form__felder">
        {defs.map((d) => {
          const id = `${basisId}-${d.key}`;
          const ro = istNurLesen(d) || (!neu && fest.includes(d.key));
          const ungueltig = fehler?.feld === d.key;
          const set = (v: string) => setWerte((w) => ({ ...w, [d.key]: v }));
          return (
            <div key={d.key} className={`crm-feld${d.art === 'textarea' ? ' crm-feld--breit' : ''}${d.art === 'ro-tech' ? ' crm-feld--tech' : ''}`}>
              <label htmlFor={id}>
                {d.label}
                {d.herkunft && <span className="crm-herkunft"> · {d.herkunft}</span>}
              </label>
              {ro ? (
                <output id={id} className="crm-feld__ro">
                  {feldText(d.key, original[d.key], d.art)}
                </output>
              ) : d.art === 'select' ? (
                <select
                  id={id}
                  value={werte[d.key] ?? ''}
                  onChange={(e) => set(e.target.value)}
                  disabled={d.gesperrt}
                  aria-invalid={ungueltig || undefined}
                  aria-errormessage={ungueltig ? `${id}-f` : undefined}
                  aria-describedby={d.status ? `${id}-s` : undefined}
                >
                  <option value="">{LEER}</option>
                  {d.optionen!.map((o) => (
                    <option key={o} value={o}>
                      {d.optionLabels?.[o] ?? wertLabel(d.key, o)}
                    </option>
                  ))}
                </select>
              ) : d.art === 'bool3' ? (
                <select id={id} value={werte[d.key] ?? ''} onChange={(e) => set(e.target.value)} aria-invalid={ungueltig || undefined} aria-errormessage={ungueltig ? `${id}-f` : undefined}>
                  <option value="">{LEER}</option>
                  <option value="true">ja</option>
                  <option value="false">nein</option>
                </select>
              ) : d.art === 'bool' ? (
                <span className="crm-check">
                  <input id={id} type="checkbox" checked={werte[d.key] === 'true'} onChange={(e) => set(e.target.checked ? 'true' : 'false')} />
                  <span aria-hidden>{werte[d.key] === 'true' ? 'ja' : 'nein'}</span>
                </span>
              ) : d.art === 'textarea' ? (
                <textarea id={id} rows={3} value={werte[d.key] ?? ''} onChange={(e) => set(e.target.value)} aria-invalid={ungueltig || undefined} aria-errormessage={ungueltig ? `${id}-f` : undefined} />
              ) : (
                <input
                  id={id}
                  type={d.art === 'datum' ? 'date' : d.art === 'zeit' ? 'datetime-local' : d.art === 'int' ? 'number' : 'text'}
                  inputMode={d.art === 'dezimal' ? 'decimal' : undefined}
                  step={d.art === 'int' ? 1 : undefined}
                  min={d.art === 'int' ? 0 : undefined}
                  value={werte[d.key] ?? ''}
                  onChange={(e) => set(e.target.value)}
                  aria-invalid={ungueltig || undefined} aria-errormessage={ungueltig ? `${id}-f` : undefined}
                  aria-describedby={d.art === 'tags' ? `${id}-h` : undefined}
                />
              )}
              {d.status && !ro && (
                <span id={`${id}-s`} className={`crm-feld__status${d.status.fehler ? ' crm-feld__status--fehler' : ''}`} role="status">
                  {d.status.text}
                  {d.status.erneut && (
                    <>
                      {' '}
                      <button type="button" className="pb-button pb-button--quiet crm-feld__erneut" onClick={d.status.erneut}>
                        Erneut laden
                      </button>
                    </>
                  )}
                </span>
              )}
              {ungueltig && (
                <span id={`${id}-f`} className="crm-feld__fehler">
                  {fehler!.text}
                </span>
              )}
              {d.art === 'tags' && !ro && (
                <span id={`${id}-h`} className="crm-muted">
                  Mehrere Tags mit Komma trennen.
                </span>
              )}
            </div>
          );
        })}
        <div className="crm-feld crm-feld--breit">
          <label htmlFor={kommentarId}>Kommentar zur Änderung (optional, landet im Änderungsprotokoll)</label>
          <input id={kommentarId} type="text" maxLength={500} value={kommentar} onChange={(e) => setKommentar(e.target.value)} />
        </div>
      </div>
      {fehler && (
        <p className="crm-hinweis crm-hinweis--fehler" role="alert">
          {fehler.feld && defs.some((d) => d.key === fehler.feld) ? `${defs.find((d) => d.key === fehler.feld)!.label}: ` : ''}
          {fehler.text}
        </p>
      )}
      <div className="crm-leiste" aria-live="polite">
        <span className="crm-muted">{neu ? `${anzahl} ${anzahl === 1 ? 'Feld' : 'Felder'} ausgefüllt` : anzahl ? `${anzahl} ungespeicherte ${anzahl === 1 ? 'Änderung' : 'Änderungen'}` : 'Keine Änderungen'}</span>
        <button type="button" className="pb-button pb-button--quiet" onClick={onAbbrechen} disabled={busy}>
          Abbrechen
        </button>
        <button type="submit" className="pb-button" disabled={busy || (!neu && anzahl === 0)}>
          {busy ? 'Wird gespeichert …' : 'Speichern'}
        </button>
      </div>
    </form>
  );
}
